import APIS from 'src/constants/ApiConfig';
import I18N from './I18NService';
import { IPageResult } from 'src/entity/BaseResult';
import { IDepartmentItem, IDepartmentQuery, IDict, IStaffDetail, IStaffSearch, StaffAuths, StaffRoles } from 'src/entity/DepartmentEntity';
import HttpService from 'src/services/HttpService';
import { message } from 'antd';
import { TreeNode } from 'antd/lib/tree-select/interface';
import { isNull, isString, isUndefined } from 'lodash';
import { PaginationProps } from 'antd/lib/pagination';

/**
 * 职员状态字典
 */
export const StaffStatusDict: IDict[] = [
  {
    value: true,
    name: '启用',
    key: 'common_status_start',
  },
  {
    value: false,
    name: '停用',
    key: 'common_status_stop',
  },
];

/**
 * 职员角色字典
 */
export const StaffRoleDict: IDict[] = [
  {
    value: StaffRoles.Assist,
    name: '操作员',
    key: 'userAdmin_operator',
  },
  {
    value: StaffRoles.Salesman,
    name: '销售员',
    key: 'departmentAdmin_salesman',
  },
];

/**
 * 职员权限字典
 */
export const StaffAuthDict: IDict[] = [
  {
    value: StaffAuths.Individual,
    name: '本人',
    key: 'departmentAdmin_auth_individual',
  },
  {
    value: StaffAuths.Department,
    name: '本部门',
    key: 'departmentAdmin_auth_department',
  },
  {
    value: StaffAuths.Institution,
    name: '全公司',
    key: 'departmentAdmin_auth_institution',
  },
];

const departmentApi = APIS.domainApi(APIS.departmentAdmin.department);
const staffApi = APIS.domainApi(APIS.departmentAdmin.staff);

/**
 * 部门管理service
 * @class DepartmentAdminService
 */
class DepartmentAdminService {
  /** 用户查询条件 */
  private staffQuery: Partial<IStaffSearch> | undefined;
  /** 根据 parent字段 分类 {parentUUID: IDepartmentItem[]} */
  private departmentParentMap = new Map<string, IDepartmentItem[]>();
  
  /**
   * 初始化分页器配置
   */
  initPagination(current = 1, pageSize = 10,) {
    const result: PaginationProps = {
      total: 0,
      defaultCurrent: 1,
      current,
      defaultPageSize: pageSize,
      pageSize,
      pageSizeOptions: ['10', '20', '30', '40'],
      showSizeChanger: true,
    };
    return result;
  }

  /** 
   * 获取用户查询条件
   */
  getStaffQuery = (initLimit = 10): Partial<IStaffSearch>  => {
    if (!this.staffQuery) {
      this.staffQuery = new IStaffSearch(initLimit);
    }
    return this.staffQuery;
  }

  /** 
   * 修改用户查询条件
   */
  updateStaffQuery = (param: Partial<IStaffSearch>): void => {
    this.staffQuery = param;
  }

  /**
   * 根据条件查询部门组织列表
   * @memberof DepartmentAdminService
   */
  fetchDepartments = async (query?: IDepartmentQuery): Promise<IDepartmentItem[]> => {
    const param: IDepartmentQuery =  query ? {...query, limit: 200, offset: 0} : {limit: 200, offset: 0};
    let result: IDepartmentItem[] = [];
    try {
      const res: IPageResult<IDepartmentItem[]> = await HttpService.getAll(departmentApi, param);
      if (res && Array.isArray(res.results)) {
        result = res.results;
      }
    } catch (error) {
      console.error('fetchDepartments faild: ', error);
      message.error('获取部门组织失败');
      throw error;
    }
    return result;
  };

  /**
   * 根据uuid获取某个部门详情
   * @memberof DepartmentAdminService
   */
  fetchDepartmentDetail = async (uuid: string): Promise<IDepartmentItem | undefined> => {
    let result: IDepartmentItem | undefined;
    try {
      result = (await HttpService.get(`${departmentApi}${uuid}/`)) as IDepartmentItem;
    } catch (error) {
      console.error('fetchDepartmentDetail faild: ', error);
      message.error('获取部门详情失败');
    }
    return result;
  };

  /**
   * 创建部门
   * @memberof DepartmentAdminService
   */
  createDepartment = async (data: Partial<IDepartmentItem>): Promise<IDepartmentItem | undefined> => {
    let result: IDepartmentItem | undefined;
    try {
      result = (await HttpService.post(departmentApi, data)) as IDepartmentItem;
    } catch (error) {
      console.error('createDepartment faild: ', error);
      message.error('新增部门失败');
    }
    return result;
  };

  /**
   * 更新部门
   * @memberof DepartmentAdminService
   */
  updateDepartment = async (data: Partial<IDepartmentItem>, uuid: string): Promise<IDepartmentItem | undefined> => {
    let result: IDepartmentItem | undefined;
    try {
      result = await (HttpService.patch(`${departmentApi}${uuid}/`, data)) as IDepartmentItem;
    } catch (error) {
      let errorText = '修改部门失败';
      if (error.response && error.response.data && error.response.data.error === 4000) {
        errorText = '父部门不能为当前部门以及其下子部门';
      }
      console.error('updateDepartment faild: ', error);
      message.error(errorText);
    }
    return result;
  };

  /**
   * 删除部门
   * @memberof DepartmentAdminService
   */
  deleteDepartment = async (uuid: string): Promise<boolean> => {
    let result = true;
    try {
      await HttpService.delete(`${departmentApi}${uuid}/`);
    } catch (error) {
      result = false;
      let errorText = '删除部门失败';
      if (error.response && error.response.data && error.response.data.error === 4032) {
        errorText = '该部门或其子部门有用户关联，需先解除用户关联';
      }
      console.error('deleteDepartment faild: ', error);
      message.error(errorText);
    }
    return result;
  };

  /**
   * 根据条件查询部门职员列表
   * @memberof DepartmentAdminService
   */
  fetchDepartmentStaffs = async (query: Partial<IStaffSearch>): Promise<IPageResult<IStaffDetail[]> | undefined> => {
    this.handleSearchParamFormat(query);
    this.updateStaffQuery(query);
    // 特殊处理部门查询字段, 按Ralph要求 前端拼接 本部门以及其子部门uuid
    const param: Partial<IStaffSearch> = {...query};
    if (param.department) {
      const departments: string[] = [param.department];
      const childDepartments = this.getChildDepartments(param.department);
      if (childDepartments) {
        const childDepartmentIds = childDepartments.map(item => item.uuid);
        departments.push(...childDepartmentIds);
      }
      param.department__in = departments.join();
      delete param.department;
    };
    let result: IPageResult<IStaffDetail[]> | undefined;
    try {
      const res: IPageResult<IStaffDetail[]> = await HttpService.get(staffApi, param);
      if (res) {
        result = res;
      }
    } catch (error) {
      console.error('fetchDepartmentStaffs faild: ', error);
      message.error('获取用户列表失败');
    }
    return result;
  }

  /**
   * 根据条件查询配药中心职员列表(临时)
   * @memberof DepartmentAdminService
   */
  fetchDrugstoreStaffs = async (query: Partial<IStaffSearch>): Promise<IPageResult<IStaffDetail[]> | undefined> => {
    this.handleSearchParamFormat(query);
    this.updateStaffQuery(query);
    const param: Partial<IStaffSearch> = {...query};
    let result: IPageResult<IStaffDetail[]> | undefined;
    try {
      const api = `${APIS.userUrl(APIS.userAdminList)}`;
      const res: IPageResult<IStaffDetail[]> = await HttpService.get(api, param);
      if (res) {
        result = res;
      }
    } catch (error) {
      console.error('fetchDepartmentStaffs faild: ', error);
      message.error('获取用户列表失败');
    }
    return result;
  }

  /**
   * 根据uuid获取某个职员详情
   * @memberof DepartmentAdminService
   */
  fetchDepartmentStaffDetail = async (uuid: string): Promise<IStaffDetail | undefined> => {
    let result: IStaffDetail | undefined;
    try {
      result = (await HttpService.get(`${staffApi}${uuid}/`)) as IStaffDetail;
    } catch (error) {
      console.error('fetchDepartmentStaffDetail faild: ', error);
      message.error('获取用户详情失败');
    }
    return result;
  };

  /**
   * 创建职员详情
   * @memberof DepartmentAdminService
   */
  createDepartmentStaff = async (data: Partial<IStaffDetail>): Promise<IStaffDetail | undefined> => {
    let result: IStaffDetail | undefined;
    try {
      result = (await HttpService.post(staffApi, data)) as IStaffDetail;
    } catch (error) {
      let errorText = '新增用户失败';
      if (error.response && error.response.data && error.response.data.error_description) {
        const errorCode = error.response.data.error_description.error as string;
        if (errorCode === 'mobile registered') {
          errorText = '该手机号已注册';
        } else if (errorCode === 'username registered') {
          errorText = '该用户名已注册';
        }
      }
      console.error('createDepartmentStaff faild: ', error);
      message.error(errorText);
    }
    return result;
  };

  /**
   * 创建职员详情
   * @memberof DepartmentAdminService
   */
  createDrugstoreStaff = async (data: Partial<IStaffDetail>): Promise<IStaffDetail | undefined> => {
    let result: IStaffDetail | undefined;
    try {
      const api = APIS.domainUrl(APIS.userAdminAdd)
      result = (await HttpService.post(api, data)) as IStaffDetail;
    } catch (error) {
      let errorText = '新增用户失败';
      if (error.response && error.response.data && error.response.data.error_description) {
        const errorCode = error.response.data.error_description.error as string;
        if (errorCode === 'mobile registered') {
          errorText = '该手机号已注册';
        } else if (errorCode === 'username registered') {
          errorText = '该用户名已注册';
        }
      }
      console.error('createDepartmentStaff faild: ', error);
      message.error(errorText);
    }
    return result;
  };

  /**
   * 更新职员详情
   * @memberof DepartmentAdminService
   */
  updateDepartmentStaff = async (data: Partial<IStaffDetail>, uuid: string): Promise<IStaffDetail | undefined> => {
    let result: IStaffDetail | undefined;
    try {
      // 先修改手机号
      if (data.mobile) {
        const isSuccessed = await this.updateDepartmentStaffMobile(uuid, data.mobile);
        if (isSuccessed) {
          result = await (HttpService.patch(`${staffApi}${uuid}/`, data)) as IStaffDetail;
        }
      }
    } catch (error) {
      console.error('updateDepartmentStaff faild: ', error);
      message.error('修改用户失败');
    }
    return result;
  };

  /**
   * 更新部门用户手机号(后台无法直接修改手机号,前端特殊处理)
   */
  private updateDepartmentStaffMobile = async (uuid: string, mobile: string): Promise<boolean> => {
    let isSuccessed = true;
    const api = `${APIS.userUrl(APIS.userAdminList)}${uuid}/`;
    try {
      await HttpService.patch(api, {mobile});
    } catch (error) {
      isSuccessed = false;
      let errorText = '手机号修改失败';
      if (error.response && error.response.data && error.response.data.error === 'mobile registered') {
        errorText = '该手机号已注册';
      }
      console.error('updateDepartmentStaffMobile faild: ', error);
      message.error(errorText);
    }
    return isSuccessed;
  }

  /**
   * 更新部门用户[启用|禁用]状态 (后台无法直接修改[启用|禁用]状态,前端特殊处理)
   */
  private updateDepartmentStaffActive = async (uuid: string, isActive: boolean): Promise<boolean> => {
    let isSuccessed = true;
    const api = `${APIS.userUrl(APIS.userAdminList)}${uuid}/`;
    try {
      await HttpService.patch(api, {is_active: isActive});
    } catch (error) {
      isSuccessed = false;
      throw error;
    }
    return isSuccessed;
  }

  /**
   * 删除职员详情
   * @memberof DepartmentAdminService
   */
  deleteDepartmentStaff = async (uuid: string): Promise<boolean> => {
    let result = true;
    try {
      await HttpService.delete(`${staffApi}${uuid}/`);
    } catch (error) {
      result = false;
      console.error('deleteDepartmentStaff faild: ', error);
      message.error('删除用户失败');
    }
    return result;
  };

  /**
   * 切换职员[启用|禁用]状态
   * @memberof DepartmentAdminService
   * @param uuid: 职员uuid
   * @param isActive: 需要切换的状态
   */
  toggleDepartmentStaffStatus = async (uuid: string, isActive: boolean): Promise<boolean> => {
    let result = true;
    try {
      // 先修改[启用|禁用]状态
      const isSuccessed = await this.updateDepartmentStaffActive(uuid, isActive);
      if (isSuccessed) {
        await HttpService.patch(`${staffApi}${uuid}/`, {is_active: isActive});
      }
    } catch (error) {
      result = false;
      console.error('deleteDepartmentStaff faild: ', error);
      const errorTextKey = isActive ? 'common_status_start' : 'common_status_disable';
      const errorText = `${I18N.get(errorTextKey)}${I18N.get('common_fail')}}!`
      message.error(errorText);
    }
    return result;
  };

  /**
   * 切换配药中心职员[启用|禁用]状态(临时)
   * @memberof DepartmentAdminService
   * @param uuid: 职员uuid
   * @param isActive: 需要切换的状态
   */
  toggleDrugstoreStaffStatus = async (uuid: string, isActive: boolean): Promise<boolean> => {
    let result = true;
    try {
      // 先修改[启用|禁用]状态
      result = await this.updateDepartmentStaffActive(uuid, isActive);
    } catch (error) {
      result = false;
      console.error('deleteDepartmentStaff faild: ', error);
      const errorTextKey = isActive ? 'common_status_start' : 'common_status_disable';
      const errorText = `${I18N.get(errorTextKey)}${I18N.get('common_fail')}}!`
      message.error(errorText);
    }
    return result;
  };

  /**
   * 处理简单查询参数对象格式化
   * 1, 字符串 前后去空格
   * 2, 去除 value 为 [undefined & null & 空字符串] 的 key
   */
  handleSearchParamFormat(param: {[key: string]: string | boolean | number | undefined}){
    for (const key in param) {
      if (param.hasOwnProperty(key)) {
        const item = param[key];
        if (isString(item)) {
          const trimedVal = item.trim();
          if (!trimedVal) {
            delete param[key];
          } else {
            param[key] = trimedVal;
          }
        } else if (isNull(item) || isUndefined(item)) {
          delete param[key];
        }
      }
    }
  }

  /**
   * 根据部门uuid获取级联部门名称
   */
  getCascadeDepartmentName = (uuid: string, list: IDepartmentItem[], separator = '-'): string => {
    const cascadeDepartments = this.getCascadeDepartments(uuid, list);
    const cascadeDepartmentNames = cascadeDepartments.map(item => item.name);
    const result = cascadeDepartmentNames.join(separator);
    return result;
  }

  /**
   * 构建 tree组件要求的嵌套结构
   * 代理商封装到最顶层
   * @param list
   * @param partnerName: 前端临时处理显示顶层部门名称显示错误的问题
   */
  buildDepartmentTreeData = (list: IDepartmentItem[]): TreeNode[] => {
    // 先根据 parent字段 分类 {parentUUID: IDepartmentItem[]}
    this.departmentParentMap.clear();
    list.forEach(item => {
      const tempKey = item.parent ? item.parent : 'top';
      let tempList = this.departmentParentMap.get(tempKey);
      if (tempList) {
        tempList.push(item);
      } else {
        tempList = [item];
      }
      this.departmentParentMap.set(tempKey, tempList);
    });

    const result = this.transferDepartmentTreeData('top', this.departmentParentMap);
    return result;
  }

  /**
   * 嵌套循环构建 treeData数据
   */
  private transferDepartmentTreeData = 
  (mapKey: string, allMap: Map<string, IDepartmentItem[]>): TreeNode[] => {
    const result: TreeNode[] = [];
    const tempList = allMap.get(mapKey);
    if (tempList) {
      tempList.forEach(item => {
        const tempKey = item.uuid;
        const children = this.transferDepartmentTreeData(tempKey, allMap);
        const isLeaf = !(children && children.length > 0);
        const targetItem: TreeNode = {
          key: item.uuid,
          value: item.uuid,
          title: item.name,
          isLeaf,
        }
        if (!isLeaf) {
          targetItem.children = children;
        }
        result.push(targetItem);
      });
    }
    return result;
  }

  /**
   * 根据部门uuid获取级联部门链
   */
  getCascadeDepartments = (uuid: string, list: IDepartmentItem[]): IDepartmentItem[] => {
    let result: IDepartmentItem[] = [];
    const matchedItem = list.find(item => item.uuid === uuid);
    if (matchedItem) {
      let superItems: IDepartmentItem[] = [];
      if (matchedItem.parent) {
        superItems = this.getCascadeDepartments(matchedItem.parent, list);
      }
      result = [...superItems, matchedItem]
    }

    return result;
  }

  /**
   * 根据部门uuid获取其所有嵌套的子级部门
   */
  getChildDepartments = (uuid: string):IDepartmentItem[] => {
    const result: IDepartmentItem[] = [];
    const childDepartments = this.departmentParentMap.get(uuid);
    if (childDepartments) {
      result.push(...childDepartments);
      childDepartments.forEach(item => {
        const childResult = this.getChildDepartments(item.uuid);
        result.push(...childResult);
      })
    }
    return result;
  }
}

export default new DepartmentAdminService();



// WEBPACK FOOTER //
// ./src/services/DepartmentAdminService.ts